import { MediaApi } from '@frontend/api-media';
import { sentry } from '@frontend/tracking';

const clearUrl = (url: string): void => {
  window.URL.revokeObjectURL(url);
};

const CONTENT_TYPE_EXTENSIONS: Record<string, string> = {
  'application/pdf': '.pdf',
  'image/png': '.png',
  'image/jpeg': '.jpg',
  'image/gif': '.gif',
  'video/mp4': '.mp4',
  'video/3gpp': '.3gp',
};

export const getImageName = ({ id, filename }: { id: string; filename: string }) => {
  const name = filename.split('/').pop();
  return name?.replace(`${id}-`, '') ?? '';
};

export const generateBlob = async (mediaId: string, mimetype?: string, locationId?: string) => {
  try {
    const { buffer, contentType: headerType } = await MediaApi.downloadMmsMediaImage(mediaId, locationId);
    return new Blob([new Uint8Array(buffer)], { type: mimetype || headerType || 'application/octet-stream' });
  } catch (error) {
    sentry.warn({
      error,
      topic: 'generate-blob',
      team: 'fe-messaging',
      addContext: {
        name: 'Blob generation context',
        context: {
          mediaId,
          errorMessage: JSON.stringify(error),
        },
      },
    });
  }
  return undefined;
};

const convertBlobToPng = async (blob: Blob): Promise<Blob> => {
  if (blob.type === 'image/png') return blob;

  const bitmap = await createImageBitmap(blob);
  const canvas = document.createElement('canvas');
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    bitmap.close();
    throw new Error('Could not get canvas context to convert image to PNG');
  }
  ctx.drawImage(bitmap, 0, 0);
  bitmap.close();

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((pngBlob) => {
      if (pngBlob) resolve(pngBlob);
      else reject(new Error('Failed to convert image to PNG'));
    }, 'image/png');
  });
};

export const copyImage = async (imageId: string, mimetype?: string, locationId?: string): Promise<void> => {
  const pngBlobPromise = generateBlob(imageId, mimetype, locationId).then((blob) => {
    if (!blob) throw new Error('Could not download image to copy to clipboard');
    return convertBlobToPng(blob);
  });

  document.body.focus(); // ensure document is focused or else the write will fail
  await navigator.clipboard.write([
    new ClipboardItem({
      'image/png': pngBlobPromise,
    }),
  ]);
};

export const downloadImage = async (mediaItem: {
  mediaId: string;
  filename: string;
  mimetype?: string;
  locationId?: string;
}) => {
  const link = window.document.createElement('a');
  link.style.display = 'none';
  const blob = await generateBlob(mediaItem.mediaId, mediaItem.mimetype, mediaItem.locationId);
  if (!blob) return;
  link.href = window.URL.createObjectURL(blob);
  const baseName = getImageName({ id: mediaItem?.mediaId, filename: mediaItem?.filename ?? '' });
  const expectedExt = CONTENT_TYPE_EXTENSIONS[blob.type];
  const hasExt = /\.[a-z0-9]+$/i.test(baseName);
  link.download = !hasExt && expectedExt ? `${baseName || mediaItem.mediaId}${expectedExt}` : baseName;
  window.document.body.appendChild(link);
  link.click();
  // cleanup
  clearUrl(link.href);
  window.document.body.removeChild(link);
};
